<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { Pencil } from 'lucide-svelte';
  import { policiesApi } from '$lib/api/policies';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { session } from '$lib/state/session.svelte';
  import { renderMarkdown } from '$lib/utils/markdown';
  import { t } from '$lib/i18n';

  const { slug }: { slug: string } = $props();

  const queryClient = useQueryClient();
  const query = createQuery(() => ({
    queryKey: ['policy', slug],
    queryFn: () => policiesApi.get(slug)
  }));

  let editing = $state(false);
  let form = $state({ title: '', body: '' });
  let saving = $state(false);
  let error = $state('');

  function startEdit() {
    if (!query.data) return;
    form = { title: query.data.title, body: query.data.body };
    error = '';
    editing = true;
  }

  async function save() {
    if (!form.title.trim() || !form.body.trim()) {
      error = 'Title and body are required.';
      return;
    }
    saving = true;
    error = '';
    try {
      await policiesApi.update(slug, form);
      queryClient.invalidateQueries({ queryKey: ['policy', slug] });
      editing = false;
      toast.success('Policy updated.');
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      saving = false;
    }
  }
</script>

<div class="tym-page">
  {#if query.isLoading}
    <div class="tym-loading-state">{t('common.loading')}</div>
  {:else if query.isError}
    <div class="tym-error-state">{t('policy.error')}</div>
  {:else if query.data}
    {#if !editing}
      <div class="mb-4 flex items-center justify-between">
        <p class="tym-section-title">{query.data.title}</p>
        {#if session.isHROrAdmin}
          <button type="button" class="btn-secondary" onclick={startEdit}>
            <Pencil class="h-4 w-4" /> Edit
          </button>
        {/if}
      </div>
      <div class="tym-card tym-markdown p-6">
        {@html renderMarkdown(query.data.body)}
      </div>
    {:else}
      <div class="space-y-4">
        <div>
          <div class="label">Title</div>
          <input class="input" bind:value={form.title} />
        </div>
        <div>
          <div class="label">Body (Markdown)</div>
          <textarea class="input font-mono" rows="16" bind:value={form.body}></textarea>
        </div>
        {#if error}<div class="tym-error-state">{error}</div>{/if}
        <div class="flex justify-end gap-2">
          <button type="button" class="btn-secondary" onclick={() => (editing = false)}>Cancel</button>
          <button type="button" class="btn-primary" disabled={saving} onclick={save}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    {/if}
  {/if}
</div>
